import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { useAuthStore } from '../auth/authStore';
import { makeApi } from '../../lib/api';

/**
 * Phase 11.5 — `$ds` field picker.
 *
 * Recognises Story-content fields whose value is shaped
 *   { $ds: "<datasource-key>", id: "<external-id>" }
 * and renders a two-stage picker:
 *   1. dropdown of all non-archived datasources in this Space
 *   2. dropdown of entries for the chosen datasource (lazy-fetched)
 *
 * Outside the JSON-shape detection in FieldEditor, this component
 * is otherwise a normal controlled <select>+<select> pair. Editors
 * who want to wire up a new datasource-ref field just type into the
 * JSON tab `{"$ds":"employees","id":""}` once — the structured
 * editor takes over from there.
 *
 * Closes the Phase 11.5 "$ds field-picker" gap from FEATURES.md:245.
 */

export interface DatasourceRef {
  $ds: string;
  id: string;
  __label?: string;
}

export function isDatasourceRef(value: unknown): value is DatasourceRef {
  return (
    typeof value === 'object'
    && value !== null
    && !Array.isArray(value)
    && '$ds' in value
    && typeof (value as { $ds: unknown }).$ds === 'string'
  );
}

interface Props {
  value: DatasourceRef;
  onChange: (next: DatasourceRef) => void;
}

export function DatasourceRefPicker({ value, onChange }: Props): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);

  // Step 1 — list datasources in this Space.
  const datasourcesQuery = useQuery({
    enabled: token !== null && spaceSlug !== null,
    queryKey: ['datasources', spaceSlug],
    queryFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('no token or no space');
      }
      const api = makeApi(token);
      const result = await api.listDatasources(spaceSlug);
      if (result.status !== 'ok') throw new Error('listDatasources failed: ' + result.status);
      return result;
    },
    staleTime: 30_000,
  });

  // Step 2 — list entries for the selected datasource (only when set).
  const entriesQuery = useQuery({
    enabled: token !== null && spaceSlug !== null && Boolean(value.$ds),
    queryKey: ['datasource-entries', spaceSlug, value.$ds],
    queryFn: async () => {
      if (token === null || spaceSlug === null || !value.$ds) {
        throw new Error('no token, space, or key');
      }
      const api = makeApi(token);
      const result = await api.listDatasourceEntries(spaceSlug, value.$ds, { limit: 200 });
      if (result.status !== 'ok') throw new Error('listDatasourceEntries failed: ' + result.status);
      return result;
    },
    staleTime: 30_000,
  });

  // Walk both layers of envelope: SDK's ManagementResult wraps the
  // server's `{data: [...]}` payload.
  const datasources = datasourcesQuery.data?.status === 'ok'
    ? datasourcesQuery.data.data.data
    : [];
  const entries = entriesQuery.data?.status === 'ok'
    ? entriesQuery.data.data.data
    : [];

  return (
    <div className="rounded border border-blue-200 bg-blue-50/40 p-2">
      <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-blue-700">
        $ds reference
      </p>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs">
          <span className="block pb-0.5 text-zinc-600">Datasource</span>
          <select
            data-testid="ds-picker-source"
            value={value.$ds}
            onChange={(e) => onChange({ $ds: e.target.value, id: '' })}
            className="w-full rounded border border-zinc-300 px-2 py-1 text-xs"
          >
            <option value="">— select —</option>
            {datasources.map((ds: { key: string; label?: string }) => (
              <option key={ds.key} value={ds.key}>
                {ds.label || ds.key}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-xs">
          <span className="block pb-0.5 text-zinc-600">Entry</span>
          <select
            data-testid="ds-picker-entry"
            value={value.id}
            onChange={(e) => onChange({ ...value, id: e.target.value })}
            disabled={!value.$ds || entriesQuery.isPending}
            className="w-full rounded border border-zinc-300 px-2 py-1 text-xs disabled:bg-zinc-100"
          >
            <option value="">
              {!value.$ds
                ? '— pick a datasource first —'
                : entriesQuery.isPending
                  ? 'loading…'
                  : entries.length === 0
                    ? 'no entries'
                    : '— select —'}
            </option>
            {entries.map((entry: { external_id: string; data?: Record<string, unknown> }) => {
              const data = (entry.data ?? {}) as Record<string, unknown>;
              const label = (data.name ?? data.title ?? data.label ?? entry.external_id) as string;
              return (
                <option key={entry.external_id} value={entry.external_id}>
                  {String(label)}
                </option>
              );
            })}
          </select>
        </label>
      </div>
      {value.$ds && value.id ? (
        <p className="mt-1 font-mono text-[10px] text-zinc-500">
          {`{"$ds":"${value.$ds}","id":"${value.id}"}`}
        </p>
      ) : null}
    </div>
  );
}
